import * as React from 'react';
import { Dialog, DialogContent } from '@/components';
import { FooterLink, FooterLinkProps } from './FooterLink';
import {
  LinkedInIcon,
  TikTokIcon,
  WeChatIcon,
  SocialLink,
  SocialLinkProps,
  SocialLinks,
  WeiboIcon,
  YoutubeIcon,
  InstagramIcon,
  XIcon,
  FacebookIcon,
} from './SocialLinks';

import {
  CurtinAllyIcon,
  AthenaSwanIcon,
  SponsorLink,
  SponsorLinkProps,
  SponsorLinks,
  AtnIcon,
  EdXIcon,
} from './SponsorLinks';
import { CampusLinks } from './CampusLinks';
import { TeachingAreasLinks } from './TeachingAreas';
import { LinksAndCricos } from './LinksAndCricos';

const acknowledgement =
  "Curtin University acknowledges Aboriginal and Torres Strait Islander people, the First Peoples of this place we call Australia, and recognises them for caring for Country for more than 60,000 years. We are honoured and grateful for the privilege to maintain campuses operating in Boorloo (Perth) and Karlkurla (Kalgoorlie) in Australia. We pay our respects to Elders past and present as Custodians and Owners of these lands. We recognise their deep knowledge and their cultural, spiritual and educational practices, and aspire to learn and teach in partnership with them. Curtin also acknowledges First Nations peoples connected with our global campuses. We are committed to working in partnership with all Custodians and Owners to strengthen and embed First Nations' voices and perspectives in our decision-making, now and into the future.";

const socialLinks: SocialLinkProps[] = [
  { to: 'https://www.facebook.com/curtinuniversity', icon: FacebookIcon },
  { to: 'https://x.com/curtinuni', icon: XIcon },
  { to: 'https://www.instagram.com/curtinuniversity/', icon: InstagramIcon },
  { to: 'https://www.youtube.com/user/CurtinUniversity?sub_confirmation=1', icon: YoutubeIcon },
  { to: 'https://www.linkedin.com/school/8788/', icon: LinkedInIcon },
  { to: 'https://www.tiktok.com/@curtinuniversity?lang=en', icon: WeiboIcon },
  { to: '/', icon: WeChatIcon },
  { to: 'https://www.tiktok.com/@curtinuniversity?lang=en', icon: TikTokIcon },
];
const sponsorLinks: SponsorLinkProps[] = [
  { to: 'https://about.curtin.edu.au/learning-teaching/empowering-people/athena-swan/', icon: AthenaSwanIcon },
  { to: 'https://students.curtin.edu.au/personal-support/lgbtiq/support/', icon: CurtinAllyIcon },
  { to: 'https://atn.edu.au/', icon: AtnIcon },
  { to: 'https://www.edx.org/school/curtinx', icon: EdXIcon },
];

const campusLinks: FooterLinkProps[] = [
  { to: 'https://about.curtin.edu.au/campus-locations/', label: 'Australia' },
  { to: 'https://www.curtincolombo.lk/', label: 'Colombo' },
  { to: 'https://curtindubai.ac.ae/', label: 'Dubai' },
  { to: 'https://www.curtin.edu.my/', label: 'Malaysia' },
  { to: 'https://curtinmauritius.ac.mu/', label: 'Mauritius' },
  { to: 'https://curtin.edu.sg/', label: 'Singapore' },
];
const teachingAreaLinks: FooterLinkProps[] = [
  { to: 'https://karda.curtin.edu.au/', label: 'Center for Aboriginal Studies' },
  { to: 'https://www.curtin.edu.au/about/learning-teaching/business-and-law/', label: 'Business and Law' },
  { to: 'https://www.curtin.edu.au/about/learning-teaching/health-sciences/', label: 'Health Sciences' },
  { to: 'https://www.curtin.edu.au/about/learning-teaching/humanities/', label: 'Humanities' },
  { to: 'https://www.curtin.edu.au/about/learning-teaching/science-engineering/', label: 'Science and Engineering' },
];

const links: FooterLinkProps[] = [
  { to: 'https://www.curtin.edu.au/copyright-statement/', label: 'Copyright' },
  { to: 'https://curtin.edu.au/disclaimer/', label: 'Disclaimer' },
  { to: 'https://curtin.edu.au/about/governance/privacy/', label: 'Privacy' },
  { to: 'https://curtin.edu.au/home/ugc-statement/', label: 'UGC Statement' },
  { to: 'https://www.curtin.edu.au/popular-links/#popular-courses', label: 'Popular Courses' },
  { to: 'https://curtin.edu.au/accessibility/', label: 'Accessibility' },
  { to: 'https://curtin.edu.au/about/contact/', label: 'Contact' },
];

export interface FooterProps extends React.HTMLAttributes<HTMLHeadingElement> {
  isFullFooter?: boolean;
  ref?: React.Ref<HTMLElement>;
}

export function Footer({ isFullFooter: externalApp, ref }: FooterProps) {
  const dialogRef = React.useRef<HTMLDivElement | null>(null);
  const [qrOpen, setQrOpen] = React.useState<boolean>(false);

  return (
    <Dialog open={qrOpen} onOpenChange={(open: boolean) => setQrOpen(open)}>
      <DialogContent ref={dialogRef} className="w-fit bg-white p-8">
        <img className="aspect-square h-64 w-64 max-w-64" src="/curtin-qr.jpg" />
      </DialogContent>
      <footer ref={ref} className="min-h-[380px] bg-black text-secondary-grey-03">
        <div
          className="mx-auto grid w-full max-w-screen-2xl auto-rows-auto grid-cols-2 items-start gap-y-8 px-5 pb-12
            grid-areas-[socials_socials,campuses_teaching-areas,acknowledgement_acknowledgement,sponsors_sponsors,links_links]
            md:grid-areas-[socials_socials,campuses_campuses,teaching-areas_teaching-areas,acknowledgement_acknowledgement,sponsors_sponsors,links_links]
            lg:gap-y-10 lg:px-10 lg:pt-[72px] lg:grid-areas-[campuses_teaching-areas,socials_sponsors,links_acknowledgement]"
        >
          {
            // Socials
            externalApp && (
              <div className="h-full w-full border-secondary-grey-02 area-[socials] lg:col-span-1 lg:border-y-2">
                <SocialLinks>
                  {socialLinks.map((link) => (
                    <SocialLink
                      key={`social-${link.icon.displayName}`}
                      to={link.to}
                      icon={link.icon}
                      onClick={
                        link.icon.displayName === WeChatIcon.displayName
                          ? (e) => {
                              e.preventDefault();
                              setQrOpen(true);
                            }
                          : undefined
                      }
                      className={'h-11'}
                    />
                  ))}
                </SocialLinks>
              </div>
            )
          }
          {
            // Campuses
            externalApp && (
              <div className="w-full area-[campuses]">
                <CampusLinks>
                  {campusLinks.map((link) => (
                    <li key={link.to}>
                      <FooterLink to={link.to} label={link.label} className={link.className} />
                    </li>
                  ))}
                </CampusLinks>
              </div>
            )
          }
          {
            // Teaching Areas
            externalApp && (
              <div className="w-full area-[teaching-areas]">
                <TeachingAreasLinks>
                  {teachingAreaLinks.map((link) => (
                    <li key={link.to} className="">
                      <FooterLink to={link.to} label={link.label} className={link.className} />
                    </li>
                  ))}
                </TeachingAreasLinks>
              </div>
            )
          }
          <div className="w-full flex-1 area-[acknowledgement]">
            <p className="w-full text-sm leading-normal">{acknowledgement}</p>
          </div>
          {
            // Sponsors
            externalApp && (
              <div className="flex h-full w-full items-center border-secondary-grey-02 area-[sponsors] lg:border-y-2">
                <SponsorLinks>
                  {sponsorLinks.map((link) => (
                    <SponsorLink key={link.icon.displayName} to={link.to} icon={link.icon} className={link.className} />
                  ))}
                </SponsorLinks>
              </div>
            )
          }
          <div className="w-full area-[links] sm:text-[14px] sm:leading-[21px]">
            <LinksAndCricos>
              {links.map((link) => (
                <li key={link.to}>
                  <FooterLink to={link.to} label={link.label} className="list-item w-1/2 md:flex md:w-fit" />
                </li>
              ))}
            </LinksAndCricos>
          </div>
        </div>
      </footer>
    </Dialog>
  );
}
